import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordRaw } from "vue-router";
import LoginView from "@/views/LoginView.vue";
import HelpView from "@/views/documentation/HelpView.vue";
import { Fetcher } from "@/services/Fetcher";
import ApplicationsView from "@/views/application/ApplicationsView.vue";
import ApplicationCreationView from "@/views/application/ApplicationCreationView.vue";
import i18n from "@/i18n";
import DataTypeAuthorizationsView from "@/views/authorizations/data/DataTypeAuthorizationsView.vue";
import DataTypeAuthorizationInfoView from "@/views/authorizations/data/DataTypeAuthorizationInfoView.vue";
import AuthorizationsManagementForApplicationCreatorView from "@/views/authorizations/gestion_app/AuthorizationsManagementForApplicationCreatorView.vue";
import AuthorizationsManagementForApplicationUsersView from "@/views/authorizations/gestion_app/AuthorizationsManagementForApplicationUsersView.vue";
import AuthorizationsRightsRequestInfoView from "@/views/authorizations/rights_request/AuthorizationsRightsRequestInfoView.vue";
import RequestAuthorizationManagementView from "@/views/authorizations/rights_request/RequestAuthorizationManagementView.vue";
import DatasManagementView from "@/views/data/DatasManagementView.vue";
import DataTableView from "@/views/data/DataTableView.vue";
import DataVersioningView from "@/views/data/DataVersioningView.vue";
import UserView from "@/views/users/UserView.vue";
import sessionService from "@/services/SessionService";
import LegalNotice from "@/components/footer/LegalNotice.vue";
import CGU from "@/components/footer/CGU.vue";
import services from "@/composable/services";
import BundleReportView from "@/views/report/BundleReportView.vue";
import ApplicationInfoView from "@/views/application/ApplicationInfoView.vue";
import AuthorizationsRightsRequestTreatmentView
  from "@/views/authorizations/rights_request/AuthorizationsRightsRequestTreatmentView.vue";
// Définit explicitement le type "any" pour éviter les erreurs avec `setTimeout` initialement implicite.
let sessionHandlerTimeout: NodeJS.Timeout | null = null;

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/login",
  },
  {
    path: "/legal-notice",
    name: "LegalNotice",
    component: LegalNotice,
  },
  {
    path: "/cgu",
    name: "GeneralConditionsOfUse",
    component: CGU,
  },
  {
    path: "/help",
    name: "Help",
    component: HelpView,
  },
  {
    path: "/login",
    name: "Login",
    component: LoginView,
    beforeEnter: (
      to: { path: string },
      from: { path: string },
      next: (route?: string | boolean) => void,
    ): void => {
      if (!sessionHandlerTimeout && from.path !== "/" && from.path !== "/login") {
        sessionService.handleExpiredSession();
        // #470 + déconnexion volontaire : on lit le marqueur posé par
        // LoginService.logout() pour distinguer les deux cas et choisir
        // le bon message de toast :
        //   - flag présent  -> déconnexion explicite , "Vous avez été déconnecté"
        //   - flag absent   -> session expirée ( inactivité / 401 ) , message
        //                       "Votre session a expiré, veuillez vous reconnecter"
        // Le flag est consommé ( removeItem ) pour ne pas se réafficher au
        // prochain passage par /login dans la même session de navigation.
        const intentional = sessionStorage.getItem("OA_INTENTIONAL_LOGOUT") === "1";
        if (intentional) {
          sessionStorage.removeItem("OA_INTENTIONAL_LOGOUT");
          services.alertService.toastSuccess((i18n.global as any).t("login.deconnected"));
        }
        else {
          services.alertService.toastSuccess((i18n.global as any).t("login.sessionExpired"));
        }

        sessionHandlerTimeout = setTimeout(() => {
          sessionHandlerTimeout = null;
        }, 50);
      }
      next();
    },
  },
  {
    path: "/login/:applicationName",
    name: "LoginWithApp",
    props: true,
    component: LoginView,
  },
  {
    path: "/users/:userIdOrLogin",
    name: "UserEdit",
    component: UserView,
    props: true,
  },
  {
    path: "/applications",
    name: "Applications",
    component: ApplicationsView,
    props: true,
  },
  {
    path: "/applications/:applicationName",
    name: "ApplicationInfoView",
    component: ApplicationInfoView,
    props: true,
  },
  {
    path: "/applicationCreation",
    name: "ApplicationCreation",
    component: ApplicationCreationView,
    props: route => ({ applicationNames: route.query.applicationNames }),
  },
  {
    path: "/authorizationsManagementForApplicationCreator",
    name: "AuthorizationsManagement",
    component: AuthorizationsManagementForApplicationCreatorView,
  },
  {
    path: "/applications/:applicationName/authorizationsManagementForApplicationUsers",
    name: "AuthorizationsUsersManagement",
    component: AuthorizationsManagementForApplicationUsersView,
    props: true,
  },
  {
    path: "/applicationCreation/:applicationName/:applicationVersion",
    name: "ApplicationUpdate",
    component: ApplicationCreationView,
    props: true,
  },
  {
    path: "/applications/:applicationName/references",
    component: DatasManagementView,
    props: true,
  },
  {
    path: "/applications/:applicationName/bundleReport",
    component: BundleReportView,
    props: true,
  },
  {
    path: "/applications/:applicationName/dataTypes",
    component: DatasManagementView,
    props: true,
  },
  {
    path: "/applications/:applicationName/dataVersioning/:dataId",
    component: DataVersioningView,
    props: true,
  },
  {
    path: "/applications/:applicationName/references/:dataId",
    component: DataTableView,
    props: true,
  },
  {
    path: "/applications/:applicationName/dataTypes/:dataId",
    component: DataTableView,
    props: true,
  },
  {
    path: "/applications/:applicationName/authorizations",
    component: DataTypeAuthorizationsView,
    props: true,
  },
  {
    path: "/applications/:applicationName/authorizations/public/:authorizationId",
    component: DataTypeAuthorizationInfoView,
    props: true,
  },
  {
    path: "/applications/:applicationName/authorizations/:authorizationId",
    component: DataTypeAuthorizationInfoView,
    props: true,
  },
  {
    path: "/applications/:applicationName/authorizationsRequest/treatment/:authorizationId",
    component: AuthorizationsRightsRequestTreatmentView,
    props: true,
  },
  {
    path: "/applications/:applicationName/authorizationsRequest/new",
    component: AuthorizationsRightsRequestInfoView,
    props: true,
  },
  {
    path: "/applications/:applicationName/authorizationsRequest",
    component: RequestAuthorizationManagementView,
    props: true,
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// #470 - Redirection sur perte d'auth ( 401 / 403 ) : on mémorise la
// route courante dans ?returnUrl pour y revenir après relogin.
// Si l'utilisateur est déjà sur /login ( ex. 401 sur POST /login pour
// mauvais identifiants , ou 401 parasite sur /session/config à l'init ) ,
// on ne redirige pas : cela empilerait ?returnUrl=/login à chaque
// tentative.
document.addEventListener("disconnected", () => {
  if (router.currentRoute.value.path === "/login") return;
  const returnUrl = sanitizeReturnUrl(router.currentRoute.value.fullPath);
  router.push({ path: "/login", query: returnUrl ? { returnUrl } : undefined });
});

// #470 - Chemins publics accessibles sans authentification. Tout le
// reste est considéré comme protégé et déclenche une redirection vers
// /login avec mémorisation de la route d'origine dans ?returnUrl=.
const PUBLIC_PATHS = new Set(["/", "/login", "/legal-notice", "/cgu", "/help"]);

function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.has(path)
    || path.startsWith("/login/")
    // BundleReportView lit ses données depuis window.name et fonctionne
    // en mode déconnecté ( partage de rapport , test offline ) : route
    // volontairement accessible sans authentification.
    || path.endsWith("/bundleReport");
}

// #470 - Bypass du guard sous Cypress : les tests existants moquent
// les appels API via cy.intercept mais ne configurent pas l'état
// d'auth frontend , et cassaient avec le guard. En production ,
// window.Cypress n'existe pas et le guard reste actif normalement.
const isRunningInCypress = typeof window !== "undefined"
  && (window as { Cypress?: unknown }).Cypress !== undefined;

function isAuthenticated(): boolean {
  return !!Fetcher.AUTHORIZATION.value
    && !!services.loginService.getStoredAuthenticatedUser();
}

// Valide qu'un returnUrl reste local ( prévention des open-redirects ) :
// on n'accepte que les chemins absolus démarrant par "/" sans "//" ni schéma.
// On rejette aussi les returnUrl pointant vers /login pour éviter une
// boucle /login?returnUrl=/login empilée à chaque tentative.
function sanitizeReturnUrl(raw: unknown): string | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  if (raw === "/login" || raw.startsWith("/login?") || raw.startsWith("/login/")) return null;
  return raw;
}

router.beforeEach((to: RouteLocationNormalized, _from: RouteLocationNormalized, next) => {
  if (isRunningInCypress || isPublicPath(to.path) || isAuthenticated()) {
    next();
    return;
  }
  const returnUrl = sanitizeReturnUrl(to.fullPath);
  next({ path: "/login", query: returnUrl ? { returnUrl } : undefined });
});

export default router;
export { sanitizeReturnUrl };
